/**
 * The console's «Modo QA» button (dev/QA only). The API says in /config.json whether the
 * environment has a QA mode at all (`feedback`: false in production) and whether the factory's
 * GitHub App is configured. Pressing the button loads the factory's feedback widget from this
 * origin and turns its QA mode on (`?factory-qa=1`); the choice is remembered in this browser,
 * and pressing it again reopens the console without the widget.
 *
 * A link does the same on any page of the console, the login screen included (no Nubi session
 * needed). The factory's standard: `?mode=qa` turns it on and `?mode=normal` off, with the
 * aliases `?modo=qa|normal` and `?factory-qa=1|0` (the widget's own switch). The link counts
 * only in shared QA environments (dev-qa) and per-PR ones — /config.json says so with `link` —
 * and is ignored locally and in production (where there is no QA mode at all).
 *
 * Pure (no DOM types): main.ts passes the browser in through `QaModeHost`.
 */

/**
 * What /config.json says: absent or false where there is no QA mode (production); otherwise
 * whether it is configured and whether a link may turn it on (`link`: QA and per-PR only).
 */
export type FeedbackSetting = false | { configured: boolean; link: boolean } | undefined;

export const WIDGET_SRC = '/__feedback/widget.js';
export const QA_MODE_KEY = 'nubi.qa-mode';
/** The widget's own switch: 1 turns its QA mode on, 0 off. */
const PARAM = 'factory-qa';
/** The link's switch, `qa` on and `normal` off: `mode` (the factory's standard), then `modo`. */
const LINK_PARAMS = ['mode', 'modo'] as const;
const LINK: Record<string, 'on' | 'off'> = { qa: 'on', normal: 'off' };
export const NOT_CONFIGURED =
  'El modo QA aún no está configurado en este ambiente: falta el Client ID de la GitHub App de la fábrica (FEEDBACK_GITHUB_CLIENT_ID).';

export interface QaModeHost {
  /** The page's address (now). */
  readonly url: string;
  /** localStorage; null (or throwing) in private windows that refuse it. */
  storage: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
  } | null;
  /** Appends the widget's <script>. */
  loadWidget(src: string): void;
  /** Changes the address without leaving the page. */
  replaceUrl(url: string): void;
  /** Opens the address (the console again, without the widget). */
  navigate(url: string): void;
  /** Tells the person something (a toast). */
  explain(message: string): void;
}

export interface QaMode {
  /** The button exists (dev/QA). */
  readonly visible: boolean;
  /** The widget is loaded and in QA mode. */
  readonly active: boolean;
  readonly label: string;
  press(): void;
}

const remembered = (host: QaModeHost) => {
  try {
    return host.storage?.getItem(QA_MODE_KEY) === 'on';
  } catch {
    return false;
  }
};
const remember = (host: QaModeHost, on: boolean) => {
  try {
    if (on) host.storage?.setItem(QA_MODE_KEY, 'on');
    else host.storage?.removeItem(QA_MODE_KEY);
  } catch {
    // Private windows may refuse storage: the QA mode still works for this visit.
  }
};
const withParam = (url: string, value: string | null) => {
  const u = new URL(url);
  for (const p of LINK_PARAMS) u.searchParams.delete(p);
  if (value === null) u.searchParams.delete(PARAM);
  else u.searchParams.set(PARAM, value);
  return u.toString();
};
/** What the address asks for: on, off or nothing. */
const askedIn = (url: string) => {
  const params = new URL(url).searchParams;
  for (const p of LINK_PARAMS) {
    const link = LINK[params.get(p) ?? ''];
    if (link) return link;
  }
  const widget = params.get(PARAM);
  return widget === '1' ? 'on' : widget === '0' ? 'off' : null;
};

export function qaMode(setting: FeedbackSetting, host: QaModeHost): QaMode {
  const visible = Boolean(setting);
  const configured = Boolean(setting && setting.configured);
  // Locally the address never switches it (only the button does).
  const asked = setting && setting.link ? askedIn(host.url) : null;
  let active = false;
  const enter = () => {
    active = true;
    remember(host, true);
    host.loadWidget(WIDGET_SRC);
  };
  if (visible && configured) {
    if (asked === 'off') {
      remember(host, false);
      host.replaceUrl(withParam(host.url, null));
    } else if (asked === 'on') {
      // One switch in the address, the widget's own: `mode=qa` / `modo=qa` become `factory-qa=1`.
      host.replaceUrl(withParam(host.url, '1'));
      enter();
    } else if (remembered(host)) enter();
  }
  return {
    visible,
    get active() {
      return active;
    },
    get label() {
      return active ? 'Salir del modo QA' : 'Modo QA';
    },
    press() {
      if (!visible) return;
      if (!configured) return host.explain(NOT_CONFIGURED);
      if (active) {
        remember(host, false);
        return host.navigate(withParam(host.url, null));
      }
      host.replaceUrl(withParam(host.url, '1'));
      enter();
    },
  };
}

/**
 * What every page does on load (qa-boot.ts, the console and the public pages alike): asks the API
 * whether this environment has a QA mode and sets it up. Without an answer there is no QA mode:
 * it must never break the page.
 */
export async function bootQaMode(
  config: () => Promise<{ feedback?: FeedbackSetting }>,
  host: QaModeHost,
): Promise<QaMode> {
  let setting: FeedbackSetting = false;
  try {
    setting = (await config()).feedback;
  } catch {
    // No /config.json (offline, an old API): the page works, without a QA mode.
  }
  return qaMode(setting, host);
}
